/**
 * StepCard – Step within a phase overview.
 * Figma Frame 41 (876:6075): 845x105px card, icon at x=20, number below icon,
 * title at x=73, description below, category badge + status button on right.
 * Figma Frame 272: status-colored step glyph (teal/grey/amber/green) + per-step time.
 */

import CategoryBadge from './CategoryBadge';
import StatusButton from './StatusButton';

import { SETTLEMENT_PLANNER_STEP_CARD } from './settlementTheme';

type StepStatus = 'not-started' | 'in-progress' | 'complete';

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  category?: string;
  status: StepStatus;
  subTaskProgress?: { completed: number; total: number };
  onClick?: () => void;
  disabled?: boolean;
  appearance?: 'default' | 'planner';
}

function StepGlyph({ stepNumber, status }: { stepNumber: number; status: StepStatus }) {
  if (status === 'complete') {
    return (
      <div className="w-[25px] h-[25px] rounded-full bg-green-600 flex items-center justify-center" aria-hidden>
        <svg className="w-3.5 h-3.5 text-white-alt" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  if (status === 'in-progress') {
    return (
      <div
        className="w-[25px] h-[25px] rounded-full border-2 border-cyan-300 bg-cyan-100 flex items-center justify-center text-[10px] font-bold text-cyan-700"
        aria-hidden
      >
        {stepNumber}
      </div>
    );
  }
  return (
    <div
      className="w-[25px] h-[25px] rounded-full border-2 border-stone-300 bg-stone-100 flex items-center justify-center text-[10px] font-bold text-stone-600"
      aria-hidden
    >
      {stepNumber}
    </div>
  );
}

export default function StepCard({
  stepNumber,
  title,
  description,
  category,
  status,
  onClick,
  disabled,
  appearance = 'default',
}: StepCardProps) {
  const isPlanner = appearance === 'planner';
  const plannerCardClassName = SETTLEMENT_PLANNER_STEP_CARD.card;

  return (
    <div
      className={`bg-white-alt min-h-[105px] flex items-center transition-all ${
        isPlanner
          ? plannerCardClassName
          : `rounded-xl border ${status === 'complete' ? 'border-green-200' : status === 'in-progress' ? 'border-orange-200' : 'border-grey-300'}`
      } ${!disabled ? `${isPlanner ? '' : 'hover:border-blue-300 hover:shadow-sm'} cursor-pointer` : 'opacity-60'}`}
      onClick={!disabled ? onClick : undefined}
      role={!disabled ? 'button' : undefined}
      tabIndex={!disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {/* Step glyph + large number — Figma Frame 272 */}
      <div className={`flex flex-col items-center gap-1 shrink-0 ${isPlanner ? SETTLEMENT_PLANNER_STEP_CARD.numberColumn : 'w-[65px] pl-[20px]'}`}>
        <StepGlyph stepNumber={stepNumber} status={status} />
        {!isPlanner && (
          <span
            className={`text-[32px] font-bold leading-none ${
              status === 'complete' ? 'text-green-600' : status === 'in-progress' ? 'text-orange-400' : 'text-grey-300'
            }`}
          >
            {stepNumber}
          </span>
        )}
      </div>

      {/* Title + description — Figma: starts at x=73 */}
      <div className={`flex-1 min-w-0 ${isPlanner ? 'py-0' : 'py-3'}`}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0">
          <h3 className={`text-black ${isPlanner ? SETTLEMENT_PLANNER_STEP_CARD.title : 'text-base font-semibold'}`}>{title}</h3>
        </div>
        <p className={`line-clamp-2 ${isPlanner ? SETTLEMENT_PLANNER_STEP_CARD.description : 'text-sm text-grey-600'}`}>{description}</p>
      </div>

      {/* Category badge + status button — Figma: right-aligned */}
      <div className={`flex items-center shrink-0 ${isPlanner ? SETTLEMENT_PLANNER_STEP_CARD.actions : 'flex-col items-end gap-2 pr-5'}`}>
        {category && <CategoryBadge category={category} appearance={isPlanner ? 'planner' : 'default'} />}
        <StatusButton status={status} onClick={onClick} disabled={disabled} appearance={isPlanner ? 'planner' : 'default'} />
      </div>
    </div>
  );
}
